import React, { useState, useEffect, ReactNode } from "react";

interface IHighlightSearchProps {
  children: ReactNode;
  // 要高亮的搜索词列表
  searchTerm?: string[];
  // 高亮颜色
  highlightColor?: string;
}

// HighlightSearch组件：用于高亮显示文本中的搜索词
const HighlightSearch: React.FC<IHighlightSearchProps> = ({
  children,
  searchTerm,
  highlightColor = '#165DFF'
}) => {
  // 内容状态，用于存储处理后的React节点
  const [content, setContent] = useState<ReactNode>(null);

  /**
   * 转义字符串中的正则特殊字符，防止正则语法错误
   * @param str 原始字符串
   * @returns 转义后的字符串
   */
  function escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
  }

  /**
   * 根据关键词列表构建一个全局不区分大小写的正则，用于匹配所有关键词
   * @param terms 关键词数组
   * @returns 合并后的正则表达式
   */
  const buildRegex = (terms: string[]) => {
    // 过滤空项并转义
    const escaped = terms
      .filter(term => term)
      .map(term => escapeRegExp(term));
    // 按长度降序排序，保证最长关键词优先匹配，避免子关键词覆盖
    escaped.sort((a, b) => b.length - a.length);
    // 构建捕获组，并添加gi修饰符（全局，不区分大小写）
    return new RegExp(`(${escaped.join("|")})`, 'gi');
  };

  /**
   * 递归遍历React节点，并在文本节点中替换匹配的关键词为<span>高亮显示</span>
   * @param node ReactNode，可以是字符串、数字、元素或数组
   * @returns 处理后的ReactNode
   */
  const traverseAndReplace = (node: ReactNode): ReactNode => {
    // 如果节点是字符串或数字，则进行文本切分和高亮替换
    if (typeof node === 'string' || typeof node === 'number') {
      const text = node.toString();
      // 如果没有提供关键词列表，则直接返回原文本
      if (!searchTerm || searchTerm.length === 0) {
        return text;
      }

      // 构建关键词正则，拆分文本
      const regex = buildRegex(searchTerm);
      const parts = text.split(regex);

      // 遍历拆分后的部分，匹配则渲染高亮span，否则渲染普通文本
      return parts.map((part, i) => {
        if (regex.test(part)) {
          return (
            <span style={{ color: highlightColor }} key={i}>
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      });
    }

    // 如果节点是一个有效的React元素，递归其子节点
    if (React.isValidElement(node)) {
      // 若元素有children属性，则克隆该元素并处理其子节点
      if (node.props.children) {
        const cloned = React.cloneElement(
          node,
          {},
          React.Children.toArray(node.props.children).map(traverseAndReplace)
        );
        return cloned;
      }
      // 如果无子节点（自闭合标签等），直接返回该元素
      return node;
    }

    // 如果节点是数组，递归处理每个元素
    if (Array.isArray(node)) {
      return node.map(traverseAndReplace);
    }

    // 其他类型（如null、undefined、布尔值等），直接返回
    return node;
  };

  // 在children或searchTerm变化时，重新处理内容
  useEffect(() => {
    // 若未提供关键词列表或列表为空，直接显示原始children
    if (!searchTerm || searchTerm.length === 0) {
      setContent(children);
      return;
    }
    // 调用递归函数处理children节点
    setContent(traverseAndReplace(children));
  }, [children, searchTerm]);


  return <>{content}</>;
};

export default HighlightSearch;
